import type { Paginated, PaginationQuery } from "@/configs/request";
import type { HostApplication, HostApplicationStatus, HostTier, VerifiedLevel } from "@/types";

import { BaseApi } from "./base.api";

export interface HostApplicationFilter extends PaginationQuery {
  status?: HostApplicationStatus;
}

/**
 * Mezbon bo'lish arizalari (PRD §12).
 *
 * Mezbonlik endi tugma emas: odam ariza yozadi, admin ko'radi. Bu
 * mashhur odamning nomidan akkaunt ochib, uning nomidan uchrashuv
 * sotishni qiyinlashtiradi — o'tib ketganini esa uchrashuvdagi
 * `impersonation` shikoyati tutadi.
 */
class AdminHostApplicationsApi extends BaseApi {
  private readonly baseUrl = "/admin/host-applications";

  list(filter: HostApplicationFilter = {}) {
    return this.request<Paginated<HostApplication>>(this.baseUrl, { query: { ...filter } });
  }

  detail(id: string) {
    return this.request<HostApplication>(`${this.baseUrl}/${id}`);
  }

  /**
   * Qaror. `approved` mezbon rejimini YOQMAYDI — u faqat yoqish
   * huquqini beradi: profil to'liqligi va tariflar baribir
   * tekshiriladi, aks holda katalogda ko'rinadigan, lekin taklif
   * qabul qila olmaydigan mezbon paydo bo'lardi.
   */
  review(id: string, outcome: "approved" | "rejected", note?: string) {
    return this.request<HostApplication>(`${this.baseUrl}/${id}/review`, {
      method: "POST",
      body: { outcome, note },
    });
  }
}

/** Ishonch darajasi va mezbonlik cheklovi. */
class AdminHostsApi extends BaseApi {
  /**
   * Ishonch darajasi (PRD §12).
   *
   * 3-daraja — profildagi yulduzcha, ya'ni platformaning "bu odam
   * haqiqatan o'sha" degan kafolati. Shuning uchun ariza ko'rishdan
   * ALOHIDA ruxsat (`hosts.verify`).
   */
  verify(userId: string, level: VerifiedLevel) {
    return this.request<{ userId: string; verifiedLevel: VerifiedLevel }>(
      `/admin/hosts/${userId}/verify`,
      { method: "POST", body: { level } },
    );
  }

  /**
   * Mezbonlik cheklovi (QD-22).
   *
   * Akkaunt bloki EMAS va qahvalar musodara qilinmaydi: hisobdagi
   * qahva odamning o'zi sotib olgan mulki. Buzilgan ishonch
   * platformaning MEZBON tomonida, shuning uchun chora ham o'sha
   * tomonga qaratiladi — odam mehmon bo'lib qolaveradi, lekin
   * katalogga qayta chiqa olmaydi.
   */
  /**
   * Mezbon toifasi (ADR 0025).
   *
   * `partner` — platforma bilan ALOHIDA shartnoma tuzgan mezbon:
   * uchrashuvlari uchun unga platformadan TASHQARIDA pul to'lanadi.
   * Qahva hech kimga pulga chiqarilmaydi, ya'ni bu farq qahva
   * tizimiga umuman tegmaydi va hech qanday to'lov mantig'i bu
   * maydonga bog'lanmagan.
   *
   * NEGA OCHIQ BELGI: farq baribir bilinadi, va yashirilgan farq
   * oddiy mezbonlarda «meni ishlatishdi» degan his qoldiradi.
   */
  setTier(userId: string, tier: HostTier) {
    return this.request<{ userId: string; tier: HostTier }>(`/admin/hosts/${userId}/tier`, {
      method: "POST",
      body: { tier },
    });
  }

  setHostBan(userId: string, banned: boolean, note?: string) {
    return this.request<{
      userId: string;
      hostBannedAt: string | null;
      hostBanNote: string | null;
    }>(`/admin/hosts/${userId}/host-ban`, { method: "POST", body: { banned, note } });
  }
}

export const adminHostApplicationsApi = new AdminHostApplicationsApi();
export const adminHostsApi = new AdminHostsApi();
